import React from 'react'
import { Card, CardBody, Form } from 'reactstrap'
import { Spin, message, Button } from 'antd'
import { getFbaBoxLabelInfo, markFbaBoxLabelled } from '../../services/api/apiBatchShip'
import isPdfUrl from '../helpers/isPdfUrl'
import printJS from 'print-js'

// FAC-23949: Màn scan & in label thùng FBA.
// Flow:
// 1. User quét mã thùng -> gọi API label-info lấy thông tin thùng, validate phải có đủ 2 label
//    (Amazon box label + ship label).
// 2. Tự mở popup in label Amazon trước, hiện nút "In label ship" trên màn quét.
// 3. Click "In label ship" -> mở popup in label ship + call API labelled đánh dấu thùng đã dán đủ label
//    (BE lưu user thao tác + thời gian).

// Response API label-info (màn Dán Label FBA): amazon_box_label + ship_label.
// (Khác với màn Lô ship / List thùng dùng label_ship_details.)
const getFbaLabelUrl = (box) => (box && box.amazon_box_label) || ''
const getShipLabelUrl = (box) => {
    const s = box && box.ship_label
    return (typeof s === 'string' ? s : (s && s.url)) || ''
}

class BoxLabelPage extends React.Component {
    inputRef = React.createRef()

    state = {
        barcode: '',
        box: null,
        boxId: null,
        batchInfo: null,
        isLoading: false,
        error: '',
        shipPrinted: false,
        markLoading: false,
    }

    _handleChange = (e) => {
        this.setState({ barcode: e.target.value })
    }

    // In label: ảnh (PNG) in qua iframe ẩn (không mở tab); PDF thì mở tab mới để xem/in.
    _openPrint = (url) => {
        if (!url) return

        if (isPdfUrl(url)) {
            // In PDF bằng print-js (giống màn Dán label thường) - mở hộp thoại in, không mở tab mới.
            printJS({ printable: url, type: 'pdf' })
            return
        }

        const iframe = document.createElement('iframe')
        iframe.style.cssText = 'position:fixed;left:-9999px;width:0;height:0;border:0'
        document.body.appendChild(iframe)

        const cleanup = () => {
            setTimeout(() => { iframe.parentNode && iframe.parentNode.removeChild(iframe) }, 1000)
        }
        const doPrint = () => {
            try {
                iframe.contentWindow.focus()
                iframe.contentWindow.print()
            } catch (e) { /* noop */ }
            cleanup()
        }

        const doc = iframe.contentWindow.document
        doc.open()
        doc.write('<!doctype html><html><head><title>In label</title></head><body style="margin:0;text-align:center"></body></html>')
        doc.close()

        const img = doc.createElement('img')
        img.style.maxWidth = '100%'
        img.onload = doPrint
        img.onerror = cleanup
        img.src = url
        doc.body.appendChild(img)
    }

    _handleSubmit = async (e) => {
        e.preventDefault()
        const { barcode } = this.state
        const vBarcode = barcode.trim()
        if (!vBarcode) return

        this.setState({ barcode: '', isLoading: true, error: '', box: null, boxId: null, batchInfo: null, shipPrinted: false })

        try {
            const boxId = vBarcode.replace('#', '').split('_')[0]
            if (!boxId) throw new Error('Mã thùng không hợp lệ')

            const { data: res } = await getFbaBoxLabelInfo(boxId)
            const { success, data, message: mess } = res
            if (!success) throw new Error(mess || 'Không lấy được thông tin thùng')

            const box = data || {}
            const batchInfo = box.batch_ship || box.batch || null

            // Validate phải có đủ 2 label trước khi cho in.
            const fbaUrl = getFbaLabelUrl(box)
            const shipUrl = getShipLabelUrl(box)
            const missing = []
            if (!fbaUrl) missing.push('Amazon box label')
            if (!shipUrl) missing.push('Ship label')
            if (missing.length > 0) {
                this.setState({ box, boxId, batchInfo, error: `Thùng #${boxId} thiếu: ${missing.join(' & ')}` })
                return
            }

            this.setState({ box, boxId, batchInfo }, () => {
                // Bước 1: tự bật màn in label Amazon trước.
                this._openPrint(fbaUrl)
            })
        } catch (err) {
            this.setState({ error: `[Mã thùng: ${vBarcode}] ${err.message}` })
        } finally {
            this.setState({ isLoading: false })
        }
    }

    _handleReset = () => {
        this.setState({ box: null, boxId: null, batchInfo: null, error: '', barcode: '', shipPrinted: false })
    }

    _handlePrintShip = async () => {
        const { box, boxId } = this.state
        if (!box) return

        // Bật màn in label ship.
        this._openPrint(getShipLabelUrl(box))

        // Đồng thời báo BE thùng đã dán đủ label (BE lưu user + thời gian).
        if (!boxId) {
            message.warning('Đã mở label ship để in, nhưng thiếu mã thùng nên chưa cập nhật được trạng thái.')
            return
        }
        this.setState({ markLoading: true })
        try {
            const { data } = await markFbaBoxLabelled(boxId)
            const { success, message: mess } = data
            if (success) {
                message.success('Đã in đủ 2 label & cập nhật trạng thái thùng!')
                // Clear thông tin & focus lại input để scan thùng tiếp theo.
                this._handleReset()
                if (this.inputRef.current) this.inputRef.current.focus()
            } else {
                message.error(`Thùng #${boxId}: ${mess || 'Cập nhật trạng thái dán label không thành công!'}`)
            }
        } catch (error) {
            message.error(`Thùng #${boxId}: ${error.message}`)
        } finally {
            this.setState({ markLoading: false })
        }
    }

    _renderLabelPreview = (url) => {
        if (!url) {
            return <div className='BoxLabelMissing blink'>Chưa có label</div>
        }

        return isPdfUrl(url)
            ? (
                <iframe
                    className='BoxLabelPreviewPdf'
                    src={`${url}#toolbar=0&navpanes=0&scrollbar=0&view=Fit`}
                    title='Label preview'
                />
            )
            : <img className='BoxLabelPreviewImage' src={url} alt='Label preview' />
    }

    _renderContent = () => {
        const { box, boxId, batchInfo, error, shipPrinted, markLoading } = this.state

        if (error) {
            return (
                <div className='ShipContent mt-3'>
                    <div className='BoxLabelMissing blink text-center'>{error}</div>
                </div>
            )
        }

        if (!box) return null

        const fbaUrl = getFbaLabelUrl(box)
        const shipUrl = getShipLabelUrl(box)

        return (
            <div className='ShipContent mt-3'>
                <Card>
                    <div className='card-header d-flex align-items-center justify-content-between'>
                        <div>
                            <strong>Thùng:</strong> #{boxId}
                            {batchInfo && batchInfo.id && (
                                <span>
                                    &nbsp;&nbsp;
                                    <strong>Lô ship:</strong> {batchInfo.id}
                                </span>
                            )}
                            {batchInfo && (batchInfo.box_carrier || batchInfo.carrier) && (
                                <span>
                                    &nbsp;&nbsp;
                                    <strong>Carrier:</strong> {batchInfo.box_carrier || batchInfo.carrier}
                                </span>
                            )}
                            {shipPrinted && (
                                <span className='badge badge-success ml-2'>Đã in đủ 2 label</span>
                            )}
                        </div>
                        <button className='btn btn-secondary' onClick={this._handleReset}>
                            Quét tiếp
                        </button>
                    </div>
                    <CardBody>
                        <div className='BoxLabelGroup'>
                            <div className='BoxLabelItem'>
                                <div className='BoxLabelItemHeader d-flex align-items-center justify-content-between'>
                                    <strong>1. Amazon box label</strong>
                                    <Button onClick={() => this._openPrint(fbaUrl)}>
                                        <i className='fa fa-print mr-1' />
                                        In lại
                                    </Button>
                                </div>
                                <div className='BoxLabelPreview'>
                                    {this._renderLabelPreview(fbaUrl)}
                                </div>
                            </div>
                            <div className='BoxLabelItem'>
                                <div className='BoxLabelItemHeader d-flex align-items-center justify-content-between'>
                                    <strong>2. Ship label</strong>
                                    <Button
                                        type='primary'
                                        loading={markLoading}
                                        onClick={this._handlePrintShip}
                                    >
                                        <i className='fa fa-print mr-1' />
                                        In label ship
                                    </Button>
                                </div>
                                <div className='BoxLabelPreview'>
                                    {this._renderLabelPreview(shipUrl)}
                                </div>
                            </div>
                        </div>
                    </CardBody>
                </Card>
            </div>
        )
    }

    render() {
        const { barcode, isLoading } = this.state

        return (
            <Spin spinning={isLoading} tip='Đang tải...'>
                <div className='ShipPage'>
                    <div className='PrintSearch'>
                        <Card>
                            <div className='card-header bg-success text-white header-elements-inline justify-content-start flex-wrap'>
                                <h1 className='card-title mr-5 mb-0'>
                                    Dán Label FBA - Quét hoặc nhập mã QR của thùng FBA
                                </h1>
                                <small className='w-100 mt-1'>
                                    Quét mã thùng để in label: Amazon box label in trước, sau đó bấm "In label ship" để in ship label.
                                </small>
                            </div>
                            <CardBody>
                                <div className='ShipSearchBody'>
                                    <Form className='d-flex justify-content-center' onSubmit={this._handleSubmit}>
                                        <input
                                            className='InputSearch form-control'
                                            name='barcode'
                                            value={barcode}
                                            onChange={this._handleChange}
                                            ref={this.inputRef}
                                            autoFocus
                                        />
                                    </Form>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                    {this._renderContent()}
                </div>
            </Spin>
        )
    }
}

export default BoxLabelPage
